import Image from "next/image";
import Link from "next/link";

/** Heading tags a guide card title may render as. Keeps callers type-safe. */
type GuideCardHeadingLevel = "h2" | "h3" | "h4";

interface GuideCardProps {
	title: string;
	description: string;
	slug: string;
	category: string;
	readingTime?: number;
	/** Localized reading time (e.g. ICU); when set, overrides default "{n} min" */
	readingTimeLabel?: string;
	heroImage?: string | null;
	heroImageAlt?: string | null;
	locale: string;
	channel: string;
	/**
	 * Heading tag for the card title. Defaults to "h2"; pass a lower level when the
	 * card is rendered underneath an existing section heading, so the document
	 * outline stays sequential for screen readers and other outline consumers.
	 */
	headingLevel?: GuideCardHeadingLevel;
}

export function GuideCard({
	title,
	description,
	slug,
	category,
	readingTime,
	readingTimeLabel,
	heroImage,
	heroImageAlt,
	locale,
	channel,
	headingLevel = "h2",
}: GuideCardProps) {
	const Heading = headingLevel;

	return (
		<Link
			href={`/${locale}/${channel}/guides/${slug}`}
			className="hover:bg-muted/50 group overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-colors"
		>
			{heroImage && (
				<div className="aspect-[16/9] overflow-hidden">
					<Image
						src={heroImage}
						alt={heroImageAlt ?? title}
						width={688}
						height={387}
						className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
					/>
				</div>
			)}
			<div className="p-5">
				<div className="mb-3 flex items-center gap-2">
					<span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
						{category}
					</span>
					{readingTime != null && (
						<span className="text-xs text-muted-foreground">{readingTimeLabel ?? `${readingTime} min`}</span>
					)}
				</div>
				<Heading className="text-base font-semibold text-foreground group-hover:underline">{title}</Heading>
				<p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
			</div>
		</Link>
	);
}
